
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { useDeleteProject, useRefreshProjects } from '@/hooks/useProjects';
import { AlertTriangle, Shield, Trash2, UserX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export function SectionPrivacy() {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium">Data & Privacy</h3>
                <p className="text-sm text-muted-foreground">
                    Control your data ownership and account security.
                </p>
            </div>
            <Separator />

            <div className="space-y-6">
                <DataPrivacyCard />
            </div>
        </div>
    );
}

function DataPrivacyCard() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [projectDeleteLoading, setProjectDeleteLoading] = useState(false);
    const [projectDeleted, setProjectDeleted] = useState(false);
    const deleteProjectMutation = useDeleteProject();
    const refreshProjects = useRefreshProjects();

    useEffect(() => {
        if (projectDeleted) {
            const timer = setTimeout(() => {
                navigate('/workspace');
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [projectDeleted, navigate]);

    const handleDeleteProject = async () => {
        if (!projectId) return;

        try {
            setProjectDeleteLoading(true);
            await deleteProjectMutation.mutateAsync(projectId);
            await new Promise(resolve => setTimeout(resolve, 100));
            refreshProjects();
            await new Promise(resolve => setTimeout(resolve, 100));
            toast({ title: "Success", description: "Project deleted successfully" });
            setProjectDeleted(true);
        } catch (error) {
            toast({ title: "Error", description: "Failed to delete project.", variant: "destructive" });
        } finally {
            setProjectDeleteLoading(false);
            setDeleteDialogOpen(false);
        }
    };

    return (
        <Card className="border-border">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-muted rounded-lg">
                        <Shield className="h-5 w-5 text-foreground" />
                    </div>
                    <div>
                        <CardTitle className="text-base">Danger Zone</CardTitle>
                        <CardDescription>
                            Irreversible actions related to your data
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                        <h4 className="font-medium text-foreground">Delete Project</h4>
                        <p className="text-sm text-muted-foreground">
                            Permanently delete this project and all its contents
                        </p>
                    </div>
                    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                        <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/20 hover:border-destructive/30">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Project
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5 text-destructive" />
                                    Delete Project Permanently
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you absolutely sure? This action cannot be undone. This will permanently delete this project and remove your data from our servers.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDeleteProject();
                                    }}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    disabled={projectDeleteLoading}
                                >
                                    {projectDeleteLoading ? "Deleting..." : "Yes, Delete Project"}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/20 opacity-75">
                    <div className="space-y-1">
                        <h4 className="font-medium text-foreground">Delete Account</h4>
                        <p className="text-sm text-muted-foreground">
                            Permanently remove your account and all data
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" disabled className="text-muted-foreground">
                        <UserX className="h-4 w-4 mr-2" />
                        Delete Account
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
